import { Control, Controller } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import useConsiderationCalculations, {
  ConsiderationPerUnitType,
  ConsiderationTotalType,
} from '../../hooks/useConsiderationCalculations';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { InputAdornment, Typography } from '@mui/material';

type Props = {
  control: Control<rules.CreateRealisationFormValues>;
  index: number;
  currencyCode: CurrencyCode;
  disabled: boolean;
};

const ManualReceivedConsiderationRow: React.FC<Props> = ({
  control,
  index,
  currencyCode,
  disabled,
}) => {
  const { onManualConsiderationChange, setFieldFocus } =
    useConsiderationCalculations();

  return (
    <FormField
      label="Received consideration"
      tooltip="This is the consideration physically received by the fund following the realisation. If there is withholding tax accounted for at the fund level, please do not include it here. Instead, add it in 'Withholding tax payable' below."
      layout="horizontal"
      breakpoint="md"
      labelSize="sm"
    >
      <Row spacing="sm">
        <Controller
          name={`holdingClassRealisation.${index}.cashConsiderationAmounts.receivedConsiderationPerUnit`}
          control={control}
          render={({
            field: { ref, onChange, ...rest },
            fieldState: { error },
          }) => (
            <NumberInput
              {...rest}
              currencyStartAdornment
              placeholder="Enter amount"
              disabled={disabled}
              currency={currencyCode}
              decimals={10}
              min={0}
              fullWidth
              onChange={(value) => {
                onChange(value);
                onManualConsiderationChange(
                  value ?? 0,
                  ConsiderationPerUnitType.Received,
                  index,
                );
              }}
              onFocus={() => {
                setFieldFocus(ConsiderationPerUnitType.Received);
              }}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2">Per unit</Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        <Controller
          name={`holdingClassRealisation.${index}.cashConsiderationAmounts.receivedConsiderationTotal`}
          control={control}
          render={({
            field: { ref, onChange, ...rest },
            fieldState: { error },
          }) => (
            <NumberInput
              {...rest}
              currencyStartAdornment
              placeholder="Enter amount"
              disabled={disabled}
              currency={currencyCode}
              decimals={2}
              min={0}
              fullWidth
              onChange={(value) => {
                onChange(value);
                onManualConsiderationChange(
                  value ?? 0,
                  ConsiderationTotalType.Received,
                  index,
                );
              }}
              onFocus={() => {
                setFieldFocus(ConsiderationTotalType.Received);
              }}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2">Total</Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Row>
    </FormField>
  );
};

export default ManualReceivedConsiderationRow;
